/* eslint react-hooks/rules-of-hooks: "warn" */
import './index.less';
import classNames from 'classnames';
import { isNumber } from 'lodash';
import React, { useEffect, useState } from 'react';
import {
    BaseOperateView,
    BaseStagePlugin,
    getFramework,
    getLayerId,
    getOperateType,
    getPointRect,
    IFramework,
    OperateClickEvents,
    PointMove,
    useActiveLayerData,
    useIdentity,
    useIsBasicOperate,
    useIsDisableMove,
    useIsDisableResize,
    useIsDisableRotate,
    useIsDisableScale,
    useLayerTranslate,
    useLoadingMap,
    useScale,
    type FormatBrushState,
} from '@/framework';
import { getLayerAgentOperateConfig } from '@/utils/agent-teams-kit';
import { isLeftButton } from '@/utils/event-kit';
import { IExportPlugin, ILayerProps, PluginType } from '@/version-types';
import { DRAW_MOUSE_OPERATE } from '../event-name';
import { resolveFormatBrushTargetId } from '../format-brush-target';
import { useGroupInnerState } from '../pick/states/group-inner-state';
import { StickyOperateHistory } from './hooks/use-sticky-info';
import { LockOperate } from './operates/lock-operate';
import { MoveOperate } from './operates/move-operate';
import { ResizeOperate } from './operates/resize-operate';
import { RotateOperate } from './operates/rotate-operate';
import { ScaleOperate } from './operates/scale-operate';
import moveState, { useMoveState } from './states/move-state';

/**
 * 获取禁用选中图层基础操作功能的操作框
 */
const useGetGroupExtensionNotPickLayerOperate = (
    framework: IFramework,
    refs: {
        refNode: React.RefObject<HTMLDivElement>;
    },
    activeData: { layer: ILayerProps | null; pickLayer: ILayerProps | null } | null,
    isFormatBrushActive: boolean,
) => {
    const operateLayer = activeData?.pickLayer || activeData?.layer;
    const behavior = activeData?.layer ? framework.service.behavior.get(activeData.layer) : null;
    const isLoading = activeData?.layer?.id ? framework.service.loading.check(activeData.layer.id) : false;
    // const isAgentLayoutEnable = framework.service.agentLayout.getEnable();

    let disableDefaultScaleOperate = activeData?.layer
        ? behavior?.disableDefaultScaleOperate?.(activeData.layer)
        : false;
    let disableDefaultResizeOperate = activeData?.layer
        ? behavior?.disableDefaultResizeOperate?.(activeData.layer)
        : false;
    let disableDefaultRotateOperate = activeData?.layer
        ? behavior?.disableDefaultRotateOperate?.(activeData.layer)
        : false;
    const disableDefaultMoveOperate = activeData?.layer
        ? behavior?.disableDefaultMoveOperate?.(activeData.layer)
        : false;

    const agentOperateConfig = getLayerAgentOperateConfig(activeData?.layer);
    if (agentOperateConfig) {
        const disable = agentOperateConfig.disable;
        disableDefaultRotateOperate = disableDefaultRotateOperate || disable?.rotate;
        disableDefaultScaleOperate = disableDefaultScaleOperate || disable?.scale;
        disableDefaultResizeOperate = disableDefaultResizeOperate || disable?.resize;
    }

    const isDisableMove = useIsDisableMove() || disableDefaultMoveOperate;
    const isDisableRotate = useIsDisableRotate() || disableDefaultRotateOperate;
    const isDisableScale = useIsDisableScale() || disableDefaultScaleOperate;
    const isDisableResize = useIsDisableResize() || disableDefaultResizeOperate;
    const isBaseOperate = useIsBasicOperate();
    useLoadingMap();

    if (!activeData?.layer || !framework.service.flatLayer.checkIsGroupExtension(activeData.layer)) return null;
    if (!operateLayer || !framework.service.flatLayer.checkDisableChildBaseOperate(operateLayer)) return null;

    const isDisableAllBaseOperate = isDisableMove && isDisableRotate && isDisableScale && isDisableResize;
    const customActiveOperate =
        isLoading || (isDisableAllBaseOperate && !agentOperateConfig?.forceCustomOperate)
            ? null
            : behavior?.customActiveOperate?.(activeData.layer) || null;
    const isLock = activeData.layer.lock;

    // console.log('isDisableMove ==> 11', isDisableMove);
    return (
        <>
            {activeData.pickLayer ? (
                <BaseOperateView layer={framework.service.flatLayer.getLayer(activeData.pickLayer.id)}>
                    <div
                        className={classNames('active-operate', {
                            'is-lock': isLock,
                            'is-basic-operate': isBaseOperate,
                            'active-operate-format-brush': isFormatBrushActive,
                        })}
                    ></div>
                </BaseOperateView>
            ) : null}
            <BaseOperateView ref={refs.refNode} layer={activeData.layer}>
                <div
                    className={classNames('active-operate', 'is-group', {
                        'is-lock': isLock,
                        'is-basic-operate': isBaseOperate,
                        'active-operate-format-brush': isFormatBrushActive,
                    })}
                >
                    {isLock ? (
                        <LockOperate layerId={activeData.layer.id} />
                    ) : (
                        <>
                            {isDisableMove ? (
                                !customActiveOperate ? (
                                    <OperateClickEvents layerId={activeData.layer.id} />
                                ) : null
                            ) : (
                                <MoveOperate layerId={activeData.layer.id} />
                            )}
                            {isDisableRotate ? null : <RotateOperate layer={activeData.layer} />}
                            {customActiveOperate}
                            {isDisableScale ? null : <ScaleOperate layer={activeData.layer} />}
                            {isDisableResize ? null : <ResizeOperate layer={activeData.layer} />}
                        </>
                    )}
                </div>
            </BaseOperateView>
        </>
    );
};

const ActiveLayer: React.FC = React.memo(() => {
    const identity = useIdentity();
    const framework = getFramework(identity);
    const activeData = useActiveLayerData();
    const [, refreshLayer] = useState(0);
    const groupOperateIsActive = useGroupInnerState()((state) => state.isActive);
    const currentActiveData = activeData
        ? {
              layer: activeData.layer?.id
                  ? framework.service.flatLayer.getLayer(activeData.layer.id) || activeData.layer
                  : null,
              pickLayer: activeData.pickLayer?.id
                  ? framework.service.flatLayer.getLayer(activeData.pickLayer.id) || activeData.pickLayer
                  : null,
          }
        : null;
    const translateLayerId = currentActiveData?.pickLayer
        ? currentActiveData.pickLayer.id
        : currentActiveData?.layer?.id;
    const refNode = useLayerTranslate(
        framework.service.flatLayer.checkIsGroupExtension(currentActiveData?.layer)
            ? currentActiveData?.layer?.id
            : translateLayerId,
        true,
    );
    const currentScale = useScale();
    const operateLayer = currentActiveData?.pickLayer || currentActiveData?.layer;
    let disableDefaultScaleOperate: boolean | undefined = undefined;
    let disableDefaultResizeOperate: boolean | undefined = undefined;
    let disableDefaultRotateOperate: boolean | undefined = undefined;
    let disableDefaultMoveOperate: boolean | undefined = undefined;
    let borderRadiusStyle: React.CSSProperties = {};
    // const isAgentLayoutEnable = framework.service.agentLayout.getEnable();
    if (operateLayer) {
        const behavior = framework.service.behavior.get(operateLayer);
        disableDefaultMoveOperate = behavior?.disableDefaultMoveOperate?.(operateLayer);
        disableDefaultScaleOperate = behavior?.disableDefaultScaleOperate?.(operateLayer);
        disableDefaultResizeOperate = behavior?.disableDefaultResizeOperate?.(operateLayer);
        disableDefaultRotateOperate = behavior?.disableDefaultRotateOperate?.(operateLayer);
        // ai 代理人禁用基础操作
        const agentOperateConfig = getLayerAgentOperateConfig(operateLayer);
        const agentDisable = agentOperateConfig?.disable;
        disableDefaultRotateOperate = disableDefaultRotateOperate || agentDisable?.rotate;
        disableDefaultScaleOperate = disableDefaultScaleOperate || agentDisable?.scale;
        disableDefaultResizeOperate = disableDefaultResizeOperate || agentDisable?.resize;
        if (agentOperateConfig?.style?.borderRadius) {
            borderRadiusStyle = {
                borderRadius: agentOperateConfig.style.borderRadius * currentScale,
            };
        }
    }
    const isBaseOperate = useIsBasicOperate();
    const isDisableMove = useIsDisableMove() || disableDefaultMoveOperate;
    const isDisableRotate = useIsDisableRotate() || disableDefaultRotateOperate;
    const isDisableScale = useIsDisableScale() || disableDefaultScaleOperate;
    const isDisableResize = useIsDisableResize() || disableDefaultResizeOperate;
    useLoadingMap();

    useEffect(() => {
        const layerIds = [currentActiveData?.layer?.id, currentActiveData?.pickLayer?.id].filter(Boolean) as string[];

        if (!layerIds.length) return;

        const handlers = layerIds.map((layerId) =>
            framework.service.flatLayer.onChange(() => {
                refreshLayer((prev) => prev + 1);
            }, layerId),
        );

        return () => {
            handlers.forEach((handler) => framework.service.events.off(handler));
        };
    }, [currentActiveData?.layer?.id, currentActiveData?.pickLayer?.id, framework]);

    useEffect(() => {
        const mouseDownInfo: { layerId: string; frameId: number | null } = { layerId: '', frameId: null };
        const moveKit = new PointMove();
        const downHandle = framework.service.editor.onMouseDown((ev) => {
            if (framework.service.editor.disableMouseSelect) return;
            if (!isLeftButton(ev)) return;

            mouseDownInfo.layerId = '';
            mouseDownInfo.frameId = null;
            moveKit.setStart({ x: ev.pageX, y: ev.pageY });

            const target = ev.target as HTMLElement;
            const formatBrushState = framework.service.formatBrush.getState();
            if (formatBrushState) {
                const mouseDownRect = getPointRect(framework.service.transform.getScenePoint(ev));
                let outerLayerId = framework.service.frame.getHoverId() || getLayerId(identity, target) || '';
                if (!framework.service.flatLayer.getLayer(outerLayerId)) {
                    outerLayerId =
                        framework.service.transform.tryPickWrap(
                            mouseDownRect,
                            framework.service.frame.getCollisionLayers(
                                framework.service.frame.getActiveFrameId(),
                                true,
                                true,
                            ),
                        ) || '';
                }
                const targetLayerId = resolveFormatBrushTargetId(framework.service, mouseDownRect, outerLayerId);
                if (framework.service.formatBrush.canApply(targetLayerId)) {
                    framework.service.formatBrush.pointerDown(targetLayerId, { continuous: ev.shiftKey });
                    return true;
                } else {
                    framework.service.formatBrush.cancel();
                }
            }

            const operateType = getOperateType(target);
            if (operateType) {
                return;
            }

            let id = framework.service.frame.getHoverId();
            const scentPoint = framework.service.transform.getScenePoint(ev);
            const mouseDownRect = getPointRect(scentPoint);
            let pickedFrameId: number | null = null;

            const mPoint = { x: ev.pageX, y: ev.pageY };
            const isLongImageMode = framework.service.edgelessCanvas.isLongImageMode;
            const stageIndex = isLongImageMode
                ? framework.service.transform.getFrameIndexByMousePointY(mPoint.y)
                : framework.service.frame.getActiveIndex();
            const isInStage = framework.service.transform.checkPointInFrame(mPoint, stageIndex);
            const isEdgelessMode = framework.service.edgelessCanvas.isEdgelessMode;

            // 无限画布下，frame 内图层的 hover 先清空，避免挡 frame 多选
            if (isEdgelessMode && framework.service.frame.checkInFrame(id)) {
                id = '';
            }

            if (!id) {
                if (isEdgelessMode || isInStage) {
                    id = framework.service.transform.tryPickWrap(
                        mouseDownRect,
                        framework.service.frame.getCollisionLayers(framework.service.frame.getActiveFrameId(), false),
                    );
                }
                if (isEdgelessMode && !id) {
                    const pickedFrame = framework.service.frame.pickWithLayerId(scentPoint);
                    pickedFrameId = pickedFrame.frameId;
                    // id = pickedFrame.layerId;
                    mouseDownInfo.layerId = pickedFrame.layerId;
                    mouseDownInfo.frameId = pickedFrameId;
                }
            }
            if (id) {
                // console.log('setActiveLayer', id);
                framework.service.frame.setHoverFrameId(null);
                framework.service.frame.setActiveLayer(id);
                framework.service.frame.setActiveIndexByLayerId(id);
                framework.service.frame.setPickActiveFrame(false);
                setTimeout(() => framework.service.frame.delHoverId(), 10);
            } else {
                // console.log('delActiveLayer ===>');
                framework.service.frame.clearFrameSelection();
                framework.service.frame.delActiveLayer();
                const selectedFrameId = pickedFrameId ?? framework.service.frame.getHoverFrameId();
                if (isNumber(selectedFrameId)) {
                    framework.service.frame.setFrameSelectionFrameIds([selectedFrameId]);
                    framework.service.frame.setHoverFrameId(null);
                    if (framework.service.frame.getFrameSelectionFrameIds().length) {
                        framework.service.frame.setPickActiveFrame(true);
                        return;
                    }
                }
                framework.service.frame.setHoverFrameId(null);
                if (isInStage) {
                    framework.service.frame.setActiveIndex(stageIndex);
                }
                framework.service.frame.setPickActiveFrame(isInStage);
            }

            // console.log(
            //     'framework.service.frame.getFrameSelectionFrameIds()',
            //     framework.service.frame.getFrameSelectionFrameIds(),
            // );
        });
        const upHandle = framework.service.editor.onMouseUp((e) => {
            if (!isLeftButton(e)) return;
            // console.log('onMouseUp mouseDownInfo', mouseDownInfo);
            moveKit.setEnd({ x: e.pageX, y: e.pageY });
            const isMove = moveKit.isMove();
            if (!isMove && mouseDownInfo.layerId && isNumber(mouseDownInfo.frameId)) {
                const selectedFrameIds = framework.service.frame.getFrameSelectionFrameIds();
                if (selectedFrameIds.length && selectedFrameIds.includes(mouseDownInfo.frameId)) {
                    framework.service.frame.setActiveLayer(mouseDownInfo.layerId);
                    framework.service.frame.setActiveIndexByLayerId(mouseDownInfo.layerId);
                }
            }
        });

        return () => {
            framework.service.events.off(downHandle);
            framework.service.events.off(upHandle);
        };
    }, []);

    useEffect(() => {
        // 激活图层变更，如果正在进行基础操作，重置基础操作状态
        return () => {
            if (framework.service.editor.isBaseOperate) {
                framework.service.editor.setIsBaseOperate(false);
            }
            // start 可能早于 isBaseOperate 写入；实例会忽略没有活动生命周期的重复 cancel。
            framework.service.registration.getInstance<MouseOperate>('MouseOperate').emitCancel();
        };
    }, [activeData?.layer?.id, framework]);

    const [formatBrushState, setFormatBrushState] = useState<FormatBrushState | null>(
        framework.service.formatBrush.getState(),
    );
    useEffect(() => framework.service.formatBrush.onChange(setFormatBrushState), [framework]);

    framework.service.events.emit(DRAW_MOUSE_OPERATE);

    const groupExtensionNotPickLayerOperate = useGetGroupExtensionNotPickLayerOperate(
        framework,
        { refNode },
        currentActiveData,
        !!formatBrushState,
    );

    if (!currentActiveData?.layer || !operateLayer || groupOperateIsActive) return null;

    const behavior = framework.service.behavior.get(operateLayer);
    const isGroup = framework.service.flatLayer.checkIsGroup(currentActiveData.layer);
    const isLoading = framework.service.loading.check(operateLayer.id);
    const isDisableAllBaseOperate = isDisableMove && isDisableRotate && isDisableScale && isDisableResize;
    const agentOperateConfig = getLayerAgentOperateConfig(operateLayer);
    const customActiveOperate =
        isLoading || (isDisableAllBaseOperate && !agentOperateConfig?.forceCustomOperate)
            ? null
            : behavior?.customActiveOperate?.(operateLayer) || null;
    const isLock = framework.service.flatLayer.checkIsNeedLock(operateLayer.id);
    const hasPickLayer = !!currentActiveData.pickLayer;
    const enableStretch = !behavior?.checkDisableStretch?.(operateLayer);
    const cursorStyle: React.CSSProperties = formatBrushState
        ? {
              pointerEvents: 'all',
          }
        : {};
    // 需要选中组外壳
    const cursorDataLayerId = formatBrushState
        ? {
              'data-layer-id': currentActiveData.layer.id,
          }
        : {};

    const disablePickLayerOperate = framework.service.flatLayer.checkDisableChildBaseOperate(
        currentActiveData.pickLayer,
    );

    // console.log('customActiveOperate ==>', customActiveOperate);

    // console.log('isDisableMove ==> 22', isDisableMove);
    return (
        <BaseStagePlugin name="active-layer" style={{ zIndex: 4 }}>
            {groupExtensionNotPickLayerOperate ? (
                groupExtensionNotPickLayerOperate
            ) : (
                <>
                    {hasPickLayer ? (
                        <BaseOperateView
                            layer={currentActiveData.layer}
                            style={{
                                zIndex: isLock ? 2 : 'auto',
                            }}
                        >
                            <div
                                className={classNames('active-operate', 'is-group', {
                                    'is-lock': isLock,
                                    'is-basic-operate': isBaseOperate,
                                    'active-operate-format-brush': !!formatBrushState,
                                })}
                                style={cursorStyle}
                                {...cursorDataLayerId}
                            >
                                {isLock ? <LockOperate layerId={currentActiveData.layer.id} /> : null}
                            </div>
                        </BaseOperateView>
                    ) : null}
                    <BaseOperateView ref={refNode} layer={operateLayer}>
                        <div
                            className={classNames(!operateLayer.__isDeformationImage ? 'active-operate' : '', {
                                'is-group': !hasPickLayer && isGroup,
                                'is-lock': isLock,
                                'is-basic-operate': isBaseOperate,
                                'active-operate-format-brush': !!formatBrushState,
                            })}
                            style={{ ...borderRadiusStyle, ...cursorStyle }}
                            {...cursorDataLayerId}
                        >
                            {disablePickLayerOperate ? null : isLock ? (
                                <>{hasPickLayer ? null : <LockOperate layerId={currentActiveData.layer.id} />}</>
                            ) : (
                                <>
                                    {isDisableMove ? (
                                        !customActiveOperate ? (
                                            <OperateClickEvents layerId={operateLayer.id} />
                                        ) : null
                                    ) : (
                                        <MoveOperate layerId={operateLayer.id} />
                                    )}
                                    {isDisableRotate ? null : <RotateOperate layer={operateLayer} />}
                                    {customActiveOperate}
                                    {enableStretch ? (
                                        <>
                                            {isDisableScale ? null : <ScaleOperate layer={operateLayer} />}
                                            {isDisableResize ? null : <ResizeOperate layer={operateLayer} />}
                                        </>
                                    ) : null}
                                </>
                            )}
                        </div>
                    </BaseOperateView>
                </>
            )}
        </BaseStagePlugin>
    );
});

/** 通过 Zustand 状态控制通用 active 组件是否挂载。 */
const RenderActive: React.FC = React.memo(() => {
    const isActiveRenderEnabled = useMoveState()((state) => state.activeRenderEnabled);

    return isActiveRenderEnabled ? <ActiveLayer /> : null;
});

const exportPlugin: IExportPlugin = {
    type: PluginType.STAGE,
    sort: 2,
    name: 'active-layer',
    render() {
        return <RenderActive />;
    },
    createState() {
        return [moveState];
    },
    createHistory({ identity }) {
        return [new StickyOperateHistory(identity)];
    },
};

export default exportPlugin;
